// --------------------------------------------------------------
// Created On: 2022-06-21
// Author: Zachary Thomas
//
// Last Modified: 2025-11-04
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useMemo } from "react";
import Chart from "react-apexcharts";
import formatDateShortLocal from "../../utilities/time/formatDateShortLocal";
import { ApexOptions } from "apexcharts";
import { BREAKPOINT_MEDIUM } from "../../constants/breakpoints";
import {
  GRAPH_DEFAULT_DECIMAL_PLACES_TO_ROUND_TO,
  GRAPH_MAX_VALUE_DIFF_TO_ENABLE_LARGE_DIFF_ROUNDING,
  GRAPH_DEFAULT_DECIMAL_PLACES_TO_ROUND_TO_FOR_LARGE_DIFFS,
  GRAPH_HEIGHT_FOR_MOBILE_DEVICES,
  BARELY_BELOW_ZERO_NUMBER,
  BARELY_ABOVE_ZERO_NUMBER,
} from "../../constants/miscellaneous";
import getSeriesUnitMap from "../../utilities/graphing/getSeriesUnitMap";
import getSeriesWillDrawYAxis from "../../utilities/graphing/getSeriesWillDrawYAxis";
import getSeriesYAxisColor from "../../utilities/graphing/getSeriesYAxisColor";
import getSeriesThatShareYAxis from "../../utilities/graphing/getSeriesThatShareYAxis";
import getShouldYAxesHaveBorders from "../../utilities/graphing/getShouldYAxesHaveBorders";

// Line chart for viewing data.
export default function LineChart(props: Props): Component {
  const options = useMemo(
    () => getOptions(props.series as TimeDataSeries[], props.axisMode, props.height),
    [JSON.stringify(props.series), props.axisMode, props.height]
  );

  // Get download file name based on if there are multiple series or not.
  function getFileName(series: TimeDataSeries[]): string {
    if (series.length === 1) {
      return `${series[0].name} History`;
    } else {
      return "Attribute History";
    }
  }

  // Get the options needed to configure the line chart.
  function getOptions(series: TimeDataSeries[], axisMode: AxisMode, height: number | string): ApexOptions | null {
    const yAxes: ApexYAxis[] = [];
    const responsiveYAxes: ApexYAxis[] = [];
    const seriesUnitMap = getSeriesUnitMap(series);
    const yAxesHaveBorders = getShouldYAxesHaveBorders(series, axisMode);

    series.forEach((singleSeries, i) => {
      const currentSeriesDrawsYAxis = getSeriesWillDrawYAxis(i, singleSeries.unitName, seriesUnitMap, axisMode);
      const seriesColor = getSeriesYAxisColor(i, singleSeries.unitName, seriesUnitMap, axisMode, series.length);
      const seriesThatShareYAxis = getSeriesThatShareYAxis(i, singleSeries.unitName, seriesUnitMap, axisMode, series);
      const maxValue = verticalBorderMax(seriesThatShareYAxis);
      const minValue = verticalBorderMin(seriesThatShareYAxis);

      yAxes.push({
        show: currentSeriesDrawsYAxis,
        seriesName: singleSeries.name,
        opposite: i % 2 !== 0,
        showAlways: axisMode === "Y_AXIS_UOM",
        max: maxValue,
        min: minValue,
        labels: {
          formatter: (value) => {
            let decimalPlaceToRoundTo = GRAPH_DEFAULT_DECIMAL_PLACES_TO_ROUND_TO;
            if (maxValue - minValue > GRAPH_MAX_VALUE_DIFF_TO_ENABLE_LARGE_DIFF_ROUNDING) {
              decimalPlaceToRoundTo = GRAPH_DEFAULT_DECIMAL_PLACES_TO_ROUND_TO_FOR_LARGE_DIFFS;
            }
            let roundedValue: number;
            if (typeof value === "string") {
              roundedValue = parseFloat(parseFloat(value).toFixed(decimalPlaceToRoundTo));
            } else {
              roundedValue = parseFloat(value.toFixed(decimalPlaceToRoundTo));
            }
            return `${roundedValue} ${singleSeries.unitShortName === null ? "" : singleSeries.unitShortName}`;
          },
          style: {
            colors: seriesColor,
          },
        },
        axisBorder: {
          show: yAxesHaveBorders,
          color: seriesColor,
        },
      });

      responsiveYAxes.push({
        show: currentSeriesDrawsYAxis,
        seriesName: singleSeries.name,
        opposite: i % 2 !== 0,
        showAlways: axisMode === "Y_AXIS_UOM",
        tickAmount: 1,
        max: maxValue,
        min: minValue,
        floating: true,
        labels: {
          offsetX: 75,
          formatter: (value) => {
            let decimalPlaceToRoundTo = GRAPH_DEFAULT_DECIMAL_PLACES_TO_ROUND_TO;
            if (maxValue - minValue > GRAPH_MAX_VALUE_DIFF_TO_ENABLE_LARGE_DIFF_ROUNDING) {
              decimalPlaceToRoundTo = GRAPH_DEFAULT_DECIMAL_PLACES_TO_ROUND_TO_FOR_LARGE_DIFFS;
            }
            let roundedValue: number;
            if (typeof value === "string") {
              roundedValue = parseFloat(parseFloat(value).toFixed(decimalPlaceToRoundTo));
            } else {
              roundedValue = parseFloat(value.toFixed(decimalPlaceToRoundTo));
            }
            return `${roundedValue} ${singleSeries.unitShortName === null ? "" : singleSeries.unitShortName}`;
          },
          style: {
            colors: seriesColor,
          },
        },
        axisBorder: {
          show: false,
          color: seriesColor,
        },
      });
    });

    // Support cached data annotations.
    const pointAnnotations: PointAnnotations[] = [];
    const dataMarkerMap = props.series[0].dataMarkerMap;
    if (props.series.length > 0 && dataMarkerMap !== undefined) {
      Object.keys(dataMarkerMap).forEach((dataMarkerKey) => {
        let dataMarker: DataMarker | null = null;
        if (dataMarkerKey in dataMarkerMap) {
          dataMarker = dataMarkerMap[dataMarkerKey];
        }

        const timestamp = parseInt(dataMarkerKey, 10);

        // Don't allow string annotations on a line chart, as they cannot be graphed.
        let value: number | null = null;
        if (dataMarker !== null) {
          if (typeof dataMarker.value === "string") {
            value = parseFloat(dataMarker.value);
          } else {
            value = dataMarker.value;
          }
        }

        if (timestamp !== null && value !== null) {
          pointAnnotations.push({
            x: timestamp,
            y: value,
            marker: {
              size: 6,
              fillColor: "var(--white)",
              strokeColor: "var(--bs-warning)",
              strokeWidth: 2,
            },
          });
        }
      });
    }

    // Set general options.
    if (series !== null && series.length > 0) {
      return {
        chart: {
          height: height,
          type: "line",
          zoom: {
            enabled: true,
            type: "x",
            autoScaleYaxis: false,
          },
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true,
            },
            export: {
              csv: {
                filename: getFileName(props.series as TimeDataSeries[]),
                columnDelimiter: ",",
                headerCategory: `${Intl.DateTimeFormat().resolvedOptions().timeZone} Time`,
                headerValue: "value",
                categoryFormatter(timestamp: number | undefined) {
                  if (timestamp === undefined) {
                    return "";
                  } else {
                    const date = new Date(timestamp);
                    return formatDateShortLocal(date.toISOString().split(".")[0]).replace(",", "");
                  }
                },
              },
              svg: {
                filename: getFileName(props.series as TimeDataSeries[]),
              },
              png: {
                filename: getFileName(props.series as TimeDataSeries[]),
              },
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: "straight",
        },
        grid: {
          row: {
            colors: ["var(--chart-background)", "var(--chart-background-alternative)"],
            opacity: 1,
          },
          padding: {
            left: 5,
            right: 0,
          },
        },
        xaxis: {
          type: "datetime",
          tickAmount: 12,
          offsetY: 9,
          labels: {
            datetimeUTC: true,
            rotate: -15,
            rotateAlways: true,
            formatter: (value: string) => {
              const date = new Date(value);
              return formatDateShortLocal(date.toISOString().split(".")[0]);
            },
          },
        },
        yaxis: yAxes,
        tooltip: {
          shared: true,
        },
        annotations: {
          points: pointAnnotations,
        },
        legend: {
          onItemClick: {
            toggleDataSeries: false,
          },
        },
        responsive: [
          {
            breakpoint: BREAKPOINT_MEDIUM,
            options: {
              chart: {
                height: GRAPH_HEIGHT_FOR_MOBILE_DEVICES,
                type: "line",
                zoom: {
                  enabled: false,
                },
                toolbar: {
                  show: true,
                  tools: {
                    download: true,
                    selection: true,
                    zoom: true,
                    zoomin: true,
                    zoomout: true,
                    pan: true,
                    reset: true,
                  },
                },
              },
              yaxis: responsiveYAxes,
              grid: {
                row: {
                  colors: ["var(--chart-background)", "var(--chart-background-alternative)"],
                  opacity: 1,
                },
                padding: {
                  left: -100,
                  right: -100,
                },
              },
            },
          },
        ],
      };
    } else {
      return null;
    }
  }

  // Calculate max y-axis border value.
  function verticalBorderMax(series: TimeDataSeries[]): number {
    let maxValue: number | null = null;
    // Find the max value.
    series.forEach((singleSeries) => {
      singleSeries.data.forEach((dataPoint) => {
        if (dataPoint.length > 1) {
          let value = dataPoint[1];
          if (typeof value === "string") {
            value = parseFloat(value);
          }
          if (maxValue === null || value > maxValue) {
            maxValue = value;
          }
        }
      });
    });

    // Add a buffer to the max value.
    if (maxValue === null) {
      maxValue = 1;
    } else if (maxValue >= BARELY_BELOW_ZERO_NUMBER && maxValue < BARELY_ABOVE_ZERO_NUMBER) {
      maxValue = 0.25;
    } else {
      maxValue = parseFloat(
        ((maxValue as number) + Math.abs(maxValue) * 0.25).toFixed(GRAPH_DEFAULT_DECIMAL_PLACES_TO_ROUND_TO)
      );
    }

    return maxValue;
  }

  // Calculate min y-axis border value.
  function verticalBorderMin(series: TimeDataSeries[]): number {
    let minValue: number | null = null;
    // Find the min value.
    series.forEach((singleSeries) => {
      singleSeries.data.forEach((dataPoint) => {
        if (dataPoint.length > 1) {
          let value = dataPoint[1] as number | string;
          if (typeof value === "string") {
            value = parseFloat(value);
          }
          if (minValue === null || value < minValue) {
            minValue = value;
          }
        }
      });
    });

    // Add a buffer to the min value.
    if (minValue === null) {
      minValue = 0;
    } else if (minValue <= BARELY_ABOVE_ZERO_NUMBER && minValue > BARELY_BELOW_ZERO_NUMBER) {
      minValue = -0.25;
    } else {
      const offset = parseFloat(
        (minValue - Math.abs(minValue) * 0.25).toFixed(GRAPH_DEFAULT_DECIMAL_PLACES_TO_ROUND_TO)
      );
      if (minValue > 0 && offset < 0) {
        minValue = 0;
      } else {
        minValue = offset;
      }
    }

    return minValue;
  }

  return options ? (
    <div className="global-style-override">
      <Chart
        options={options}
        series={props.series === null ? [] : (props.series as TimeDataSeries[])}
        type="line"
        height={props.height}
      />
    </div>
  ) : null;
}

interface Props {
  series: VariousTimeDataSeries[];
  axisMode: AxisMode;
  height: number | string;
}

interface VariousTimeDataSeries {
  name: string;
  unitName: string;
  unitShortName: string;
  data: [number, number | string][];
  dataMarkerMap?: DataMarkerMap;
}

interface DataMarkerMap {
  [key: string]: DataMarker;
}

interface DataMarker {
  value: number | string;
}

interface TimeDataSeries {
  name: string;
  unitName: string;
  unitShortName: string;
  data: [number, number][];
}

type AxisMode = "Y_AXIS_ATTRIBUTE" | "Y_AXIS_UOM";
